import moment from "moment";
import { SharedState } from "@/common/state";
import { AI_ACTIVITIES, AiFileActivityId, resolve_activity } from "../../data/model";
import { as_displayed_actor, genai_app_name, is_genai_app } from "@/common/ai-agent";

/**
 * The Gen AI events list, as gen-ai-ui draws it, over the app's live event
 * rows (`eventParser` output). Everything the table, the row menu and the
 * details dialog show goes through `to_event_view`, so none of them reaches
 * into the raw event shape.
 */

export type Level = "high" | "medium" | "low" | "none";

export const LEVEL_OF: Record<number, Level> = {
  3: "high",
  2: "medium",
  1: "low",
  0: "none",
};

export const NUM_OF: Record<Level, number> = { high: 3, medium: 2, low: 1, none: 0 };

/** gen-ai-ui visualTokens.SEVERITY labels */
export const SEVERITY_LABEL: Record<Level, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
  none: "No Risk",
};

/** gen-ai-ui visualTokens.CLASSIFICATION labels */
export const SENSITIVITY_LABEL: Record<Level, string> = {
  high: "High Sensitivity",
  medium: "Medium Sensitivity (Notice)",
  low: "Low Sensitivity",
  none: "Unclassified",
};

export const SENSITIVITY_SHORT: Record<Level, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
  none: "Unclassified",
};

export interface EventPerson {
  id?: string;
  name: string;
  email?: string;
  department?: string;
  type?: string;
  isExternal?: boolean;
  isPersonalAddress?: boolean;
}

export interface EventFile {
  id: string;
  name: string;
  /** the information label the file carries, e.g. "Personal Documentation" */
  label: string;
  /** its category, when the label sits under one, e.g. "Personal" */
  category: string;
  /** the top-level Cognni information category, e.g. "Financial" */
  info_category: string;
  sensitivity: Level;
  /** personal identifiers found in it */
  identifiers: string[];
  clusterUID?: string;
}

/**
 * Why a row is in a list that was asked for one relation (frontend fix list
 * F4, Oct 2026).
 *
 * The events query filters on a relation but answers with whole events: one
 * event can group several files, and neither the event nor its files say
 * which of them matched. So a relation filter is not a statement about the
 * row — an "UploadTo_AI" list can hold an event whose own story is Copilot
 * sharing a file back to an employee. `level` records which case a row is:
 *
 * - "item": the row names its own relation (a content item, a demo row), and
 *   it can be shown as that activity.
 * - "event": only the list's filter does. The row is a related event; its
 *   files and its story describe the event, not the match.
 */
export interface EventMatch {
  level: "item" | "event";
  /** the relation(s) the list was asked for, as the UI names them */
  relation: string;
  /** the category the list was asked for; "" when it was every category */
  category: string;
  /** the period the list was asked for, as the dashboard prints it */
  period: string;
  /** an "item" match inside a larger event: the file that matched */
  file_id?: string;
  file_name?: string;
}

/** how a row reads when only the list's filter names its relation */
export const RELATED_EVENT = "Related event";

export interface EventView {
  id: string;
  /** the live row, for the app's own actions (investigate, manage trust) */
  raw: any;
  activity_id: AiFileActivityId | null;
  activity: string;
  agent: EventPerson & { sub: string };
  /** the person on the other side of the activity */
  user: EventPerson | null;
  users: EventPerson[];
  content: { name: string; extra: number; files: string[]; tag: string };
  files: EventFile[];
  date: string;
  ts: number;
  risk: Level;
  sensitivity: Level;
  /** the risk types that fired, from the event's insights */
  risk_names: string[];
  app: string;
  story: string;
  /** set by a relation-scoped list; absent when the list asked for no relation */
  match?: EventMatch;
  /**
   * Fields the row's data did not carry. The grid keeps their columns and
   * shows "—": a missing value is a gap in the data, to be fixed there.
   */
  missing?: ("agent" | "user" | "risk")[];
}

const APP_NAMES: Record<string, string> = {
  sharepoint: "SharePoint",
  onedrive: "OneDrive",
  ms_teams: "Teams",
  exchange_365: "Outlook",
  gdrive: "Google Drive",
};

/** the application's display name: "co_pilot" reads "Microsoft 365 Copilot" (task 25233) */
const app_name_of = (source: unknown): string => {
  const s = String(source || "");
  if (s && is_genai_app(s)) return genai_app_name(s);
  return APP_NAMES[s.toLowerCase()] || s;
};

const to_level = (value: unknown): Level => {
  if (typeof value === "number") return LEVEL_OF[Math.max(0, Math.min(3, Math.round(value)))];
  const s = String(value || "").toLowerCase();
  if (s.startsWith("high")) return "high";
  if (s.startsWith("med")) return "medium";
  if (s.startsWith("low")) return "low";
  const n = Number(s);
  return Number.isFinite(n) && s !== "" ? to_level(n) : "none";
};

const MORE_SENSITIVE: Level[] = ["high", "medium", "low", "none"];
const most_sensitive = (levels: Level[]): Level =>
  MORE_SENSITIVE.find((l) => levels.includes(l)) || "none";

const label_of = (id?: string) =>
  id ? (SharedState.allInfoTypes.value || {})[id] : undefined;

/**
 * The top-level Cognni information category a label sits under ("Financial",
 * "HR"), read up its parents; "" when the label is unknown. The "Information"
 * suffix is dropped so prose can say "Financial information".
 */
const info_category_of = (id?: string): string => {
  let label = label_of(id);
  for (let hops = 0; label && label.parentId && hops < 10; hops++) {
    const parent = label_of(label.parentId);
    if (!parent) break;
    label = parent;
  }
  const name = label ? label.shortName || label.labelName || label.name || "" : "";
  return String(name).replace(/\s+Information$/i, "");
};

/** an AI agent rather than a person, by its type or its name */
const is_ai_entity = (p?: EventPerson | null) =>
  !!p &&
  (/^(ai|aiagent|ai_agent|agent|application|app|bot)$/i.test(String(p.type || "")) ||
    /co[\s-]?pilot|chatgpt|gemini/i.test(String(p.name || "")));

/**
 * The event's activity, by whatever code or display name the backend gave.
 * A live AI event carries no relation of its own (its subtype is
 * "NonHumanInteraction"), so it reads "AI Activity" unless the list was
 * asked for one relation, which the modal then applies.
 */
const activity_of = (row: any): { id: AiFileActivityId | null; label: string } => {
  const raw = String(row.event_type || "");
  const id =
    resolve_activity(raw) ||
    resolve_activity(String((row.items && row.items[0] && row.items[0].subType) || ""));
  const found = id ? AI_ACTIVITIES.find((a) => a.id === id) : undefined;
  return { id, label: found ? found.name : "AI Activity" };
};

export const format_event_date = (date: string) =>
  date ? moment(date).format("MMM D, YYYY - h:mm A") : "";

export const to_event_view = (row: any): EventView => {
  const items: any[] = row.items || [];
  const shown_label =
    (row.display_item && row.display_item.main_label && row.display_item.main_label.name) || "";
  const files: EventFile[] = items
    .filter((it) => it && it.name)
    .map((it, index) => {
      // the first file is the one the row names; when it carries no label of
      // its own, it still has the one the row shows
      const info =
        (it.labels && it.labels.infoLabel) || (index === 0 ? { name: shown_label } : {});
      const label = label_of(info.id);
      const parent = label && label.parentId ? label_of(label.parentId) : undefined;
      return {
        id: it.id,
        name: it.name,
        label: info.name || "",
        category: parent ? parent.shortName || parent.labelName || "" : "",
        info_category: info_category_of(info.id),
        sensitivity: to_level(label ? label.sensitivityLevel : 0),
        identifiers: ((it.labels && it.labels.piis) || []).map((p: any) => p.name),
        clusterUID: it.clusterUID,
      };
    });

  // The AI agent and the user by what they are, not by position: an upload
  // can come with the person as exposed-by and the agent as exposed-to, and
  // read by position the two swapped places (bug 25232).
  let by: EventPerson | null = row.exposedBy || null;
  let users: EventPerson[] = (row.exposedTo || []).filter(Boolean);
  const ai_in_users = users.find(is_ai_entity);
  if (by && !is_ai_entity(by) && ai_in_users) {
    users = [by, ...users.filter((u) => u !== ai_in_users && !is_ai_entity(u))];
    by = ai_in_users;
  }
  const main = row.display_item || (files[0] ? { name: files[0].name, main_label: { name: files[0].label } } : null);
  const { id: activity_id, label: activity } = activity_of(row);

  return {
    id: row.id,
    raw: row,
    activity_id,
    activity,
    agent: {
      ...(by || {}),
      name: (by && by.name) || "",
      sub: by && by.name ? "AI Agent" : "",
    },
    user: users[0] || null,
    users,
    content: {
      name: (main && (main.archive_name || main.name)) || "",
      extra: Math.max(0, files.length - 1),
      files: files.map((f) => f.name),
      tag: (main && main.main_label && main.main_label.name) || (files[0] && files[0].label) || "",
    },
    files,
    date: format_event_date(row.date),
    ts: row.date ? moment(row.date).valueOf() : 0,
    risk: to_level(row.severity),
    sensitivity: most_sensitive(files.map((f) => f.sensitivity)),
    risk_names: (row.insights || []).map((i: any) => i && i.name).filter(Boolean),
    app: app_name_of(row.source),
    story: row.story || "",
  };
};

/**
 * A content item's sensitivity: its level when it names one ("High", or 1..3),
 * otherwise its score (0.85 / 0.5 / 0.2 / 0), which is what the virtual labels
 * grade on.
 */
const item_sensitivity = (item: any): Level => {
  const named = item.sensitivityLevel;
  if (typeof named === "string" && named) return to_level(named);
  if (typeof named === "number" && named >= 1) return to_level(named);
  const score = Number(item.sensitivity);
  if (!Number.isFinite(score)) return "none";
  if (score >= 0.85) return "high";
  if (score >= 0.5) return "medium";
  if (score >= 0.2) return "low";
  return "none";
};

/** an `exposedBy` / `exposedTo` block, as aiContentItems returns it */
const person_of = (p: any): EventPerson | null => {
  if (!p || !(p.name || p.id)) return null;
  // an AI agent reads by its display name (task 25233)
  const shown = as_displayed_actor(p);
  return { id: shown.id, name: shown.name || "", type: shown.type };
};

/**
 * A row of a content drill-down: one file of a matching AI event
 * (`aiContentItems`, PBI 24967), drawn in the same grid as every other list.
 * The row's activity is the list's own relation when the list asked for one;
 * the AI agent is `exposedBy`, the user the first of `exposedTo`, the risk the
 * file's `severity`. A row the data leaves one of them out of reads "—" there
 * — a gap in the data, not the grid's to fill. The event itself
 * (`raw.eventRefId`, `event(id:)`) is read for the details and row actions.
 */
export const content_item_to_event_view = (
  item: any,
  relation: { id: AiFileActivityId | null; name: string } | null
): EventView => {
  const info = (item.labels && item.labels.infoLabel) || {};
  const label = label_of(info.id);
  const parent = label && label.parentId ? label_of(label.parentId) : undefined;
  const sensitivity = item_sensitivity(item);
  // roles by what each entity is, as on events (bug 25232)
  let agent = person_of(item.exposedBy);
  let users = ((item.exposedTo || []) as any[]).map(person_of).filter(Boolean) as EventPerson[];
  const ai_in_users = users.find(is_ai_entity);
  if (agent && !is_ai_entity(agent) && ai_in_users) {
    users = [agent, ...users.filter((u) => u !== ai_in_users && !is_ai_entity(u))];
    agent = ai_in_users;
  }
  const has_severity =
    item.severity !== null && item.severity !== undefined && item.severity !== "";
  const file: EventFile = {
    id: item.id,
    name: item.name || "",
    label: info.name || "",
    category: parent ? parent.shortName || parent.labelName || "" : "",
    info_category: info_category_of(info.id),
    sensitivity,
    identifiers: ((item.labels && item.labels.piis) || []).map((p: any) => p.name),
    clusterUID: item.clusterUID,
  };

  return {
    id: item.id,
    raw: item,
    activity_id: relation ? relation.id : null,
    activity: relation ? relation.name : "AI Activity",
    agent: { ...(agent || { name: "" }), sub: agent ? "AI Agent" : "" },
    user: users[0] || null,
    users,
    content: { name: file.name, extra: 0, files: [file.name], tag: file.label || item.path || "" },
    files: [file],
    date: format_event_date(item.createdAt),
    ts: item.createdAt ? moment(item.createdAt).valueOf() : 0,
    risk: has_severity ? to_level(item.severity) : "none",
    sensitivity,
    risk_names: [],
    app: app_name_of(item.sourceApplication),
    story: "",
    missing: [
      ...(agent ? [] : ["agent" as const]),
      ...(users.length ? [] : ["user" as const]),
      ...(has_severity ? [] : ["risk" as const]),
    ],
  };
};

export const department_line = (p?: EventPerson | null) =>
  p && p.department ? `Department: ${p.department}` : "";

/**
 * The phrase beside the severity badge: the activity, in the same verbs as the
 * summary. Falls back to the risk type that fired.
 */
const SITUATION: Record<string, string> = {
  shared: "Content uploaded to AI",
  uploaded: "Content uploaded to AI",
  created: "Content created by AI",
  exposed: "Content read by AI",
  modified: "Content modified by AI",
};

export const risk_situation = (e: EventView) =>
  (e.risk !== "none" && e.activity_id && SITUATION[e.activity_id]) ||
  e.risk_names[0] ||
  "involved in an AI activity";

const SENSITIVITY_WORD: Record<Level, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
  none: "",
};

/** ["A"] -> "A" ; ["A","B"] -> "A and B" ; ["A","B","C"] -> "A, B, and C" */
const join_list = (items: string[]) => {
  const list = items.filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`;
};

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** one file by name; several by their count */
const content_phrase = (e: EventView) => {
  const count = e.files.length || (e.content.name ? 1 : 0);
  if (count > 1) return `${count} files`;
  const name = (e.files[0] && e.files[0].name) || e.content.name;
  return name ? `"${name}"` : "content";
};

/** the people the event names: one by name, several as "A and N others" */
const people_phrase = (users: EventPerson[]) => {
  const named = users.filter((u) => u && (u.name || u.email));
  if (!named.length) return "";
  const first = named[0].name || named[0].email || "";
  const others = named.length - 1;
  return others ? `${first} and ${others} other${others === 1 ? "" : "s"}` : first;
};

/**
 * The risk, in one sentence after the action, when the data supports it: the
 * person, and each affected category with the sensitivities its own files
 * carry — never the first file's category or the highest sensitivity applied
 * to all of them.
 */
const risk_sentence = (e: EventView, people: string, many: boolean) => {
  if (e.risk === "none" || !people) return "";
  const by_category = new Map<string, Set<Level>>();
  e.files.forEach((f) => {
    if (!f.info_category) return;
    if (!by_category.has(f.info_category)) by_category.set(f.info_category, new Set());
    (by_category.get(f.info_category) as Set<Level>).add(f.sensitivity);
  });
  if (!by_category.size) return "";
  const parts = [...by_category.entries()].map(([category, levels]) => {
    const words = MORE_SENSITIVE.filter((l) => levels.has(l))
      .map((l) => SENSITIVITY_WORD[l])
      .filter(Boolean);
    return words.length
      ? `${category} information (${join_list(words)} sensitivity)`
      : `${category} information`;
  });
  return `${people} ${many ? "are" : "is"} outside the trusted audience for ${join_list(parts)}.`;
};

/**
 * The overview paragraph (bug 25232), built from the event itself rather than
 * the backend's triage text, which used one "shared" sentence for every
 * activity:
 *
 *   Uploaded to AI   {user} uploaded {content} to {aiAgent}.
 *   Generated by AI  {aiAgent} created {content} for {user}.
 *   Read by AI       {aiAgent} read {content} for {user}.
 *   Modified by AI   {aiAgent} modified {content} for {user}.
 *
 * {content} is the quoted file name, or "N files" for a grouped event; the
 * full list stays on the Content tab. "for {user}" appears only when the event
 * names one. The risk follows as its own sentence. Anything the event does not
 * carry is left out, never filled in.
 */
export const overview_narrative = (e: EventView) => {
  const agent = e.agent.name || "an AI agent";
  const people = people_phrase(e.users.length ? e.users : e.user ? [e.user] : []);
  const many = e.users.filter((u) => u && (u.name || u.email)).length > 1;
  const content = content_phrase(e);
  const for_people = people ? ` for ${people}` : "";
  const action = (() => {
    switch (e.activity_id) {
      case "shared":
      case "uploaded":
        return people
          ? `${people} uploaded ${content} to ${agent}.`
          : `${capitalise(content)} ${content.endsWith("files") ? "were" : "was"} uploaded to ${agent}.`;
      case "created":
        return `${agent} created ${content}${for_people}.`;
      case "exposed":
        return `${agent} read ${content}${for_people}.`;
      case "modified":
        return `${agent} modified ${content}${for_people}.`;
      default:
        return people
          ? `${agent} used ${content} while interacting with ${people}.`
          : `${agent} used ${content}.`;
    }
  })();
  return [capitalise(action), risk_sentence(e, people, many)].filter(Boolean).join(" ");
};
